import { CheckboxState } from '~src/commonInfra/components/checkbox/CheckboxState';
import { ReactCheckboxTristate } from '~src/commonInfra/components/checkbox/ReactCheckboxTristate';

import { ISiteNameWithId } from '../../../siteManagement/serverApi/types/ISiteNameWithId';
import { ADMIN_PROFILE_NONE_VALUE } from '../../helpers/AdminProfileConstants';
import { DefaultSiteRole, SiteRoleOption, SiteRoleOptionsResource } from '../../services/resources/SiteRoleOptions';
import { SiteRolesResource } from '../../services/resources/SiteRoles';
import { SiteRoles } from '../../users/serverApi/types/ISiteRole';
import { UserRoleSelections } from '../EditSiteMembershipDialog';
import { SiteRolePickerMenu } from '../siteRolePicker/SiteRolePickerMenu';
import { SiteRoleCheckboxPropsDictionary, SiteRoleSelectorPropsDictionary, SiteRolesPickerContainerState } from '../SiteRolesPickerContainer';

const ADMIN_SITE_ROLE_OPTION = SiteRoles.ServerAdministrator;

export class SiteRolesPickerDataGridHelper {

    /**
     *
     * @param state
     * @param busy
     * Update disabled state based upon if busy
     */
    public static getStateOnBusyChange(state: SiteRolesPickerContainerState, busy: boolean): SiteRolesPickerContainerState {
        let siteRowCheckboxPropMap: SiteRoleCheckboxPropsDictionary = {};
        let siteRowDropdownPropMap: SiteRoleSelectorPropsDictionary = {};
        for (let prop in state.siteRowCheckboxPropMap) {
            let disabled = busy;
            siteRowCheckboxPropMap[prop] = { ...state.siteRowCheckboxPropMap[prop], disabled };
        }
        for (let prop in state.siteRowDropdownPropMap) {
            let isDisabled = busy;
            siteRowDropdownPropMap[prop] = { ...state.siteRowDropdownPropMap[prop], isDisabled };
        }
        return { ...state, siteRowCheckboxPropMap, siteRowDropdownPropMap };
    }

    /**
     * Updates checkbox, dropdown, and admin profile state when user role selections change (e.g. selected users or their site roles).
     */
    public static getStateOnModifyRoles(state: SiteRolesPickerContainerState, userRoleSelections: UserRoleSelections, siteRolesResource: SiteRoleOptionsResource): SiteRolesPickerContainerState {
        let siteRowCheckboxPropMap: SiteRoleCheckboxPropsDictionary = {};
        let siteRowDropdownPropMap: SiteRoleSelectorPropsDictionary = {};
        let adminCheckboxState: CheckboxState;
        const siteRowAdminProfileMap: Record<string, string> = { ...state.siteRowAdminProfileMap };

        adminCheckboxState = SiteRolesPickerDataGridHelper.getAdminCheckboxStateOnModify(userRoleSelections, adminCheckboxState);

        for (let prop in state.siteRowCheckboxPropMap) {

            if (adminCheckboxState === CheckboxState.checked) {
                siteRowCheckboxPropMap[prop] = { ...state.siteRowCheckboxPropMap[prop], checkboxState: CheckboxState.checked, disabled: true };
            } else if (adminCheckboxState === CheckboxState.mixed) {
                siteRowCheckboxPropMap[prop] = { ...state.siteRowCheckboxPropMap[prop], checkboxState: CheckboxState.mixed, disabled: true };
            } else {
                siteRowCheckboxPropMap[prop] = SiteRolesPickerDataGridHelper.getRowCheckboxPropsOnModify(userRoleSelections, prop, siteRowCheckboxPropMap, state);
            }
        }

        for (let prop in state.siteRowDropdownPropMap) {

            if (adminCheckboxState === CheckboxState.checked) {
                let selectedRole = siteRolesResource.getSiteRoleOptionBySiteRole(SiteRoles.ServerAdministrator);
                let siteRoleOptions = siteRolesResource.getSiteRoleOptionsForCurrentUser();
                siteRowDropdownPropMap[prop] = { ...state.siteRowDropdownPropMap[prop], selectedRole: selectedRole, isDisabled: true, siteRoleOptions: siteRoleOptions };
            } else if (adminCheckboxState === CheckboxState.mixed) {
                let selectedRole = siteRolesResource.getSiteRoleOptionBySiteRole(SiteRolesResource.Mixed);
                siteRowDropdownPropMap[prop] = { ...state.siteRowDropdownPropMap[prop], selectedRole: selectedRole, isDisabled: true };
            } else {
                siteRowDropdownPropMap[prop] = SiteRolesPickerDataGridHelper.getRowDropdownPropsOnModify(userRoleSelections, prop, siteRowDropdownPropMap, state, siteRolesResource);
            }
            const siteInfo = userRoleSelections.userCountsAndRolesBySiteId[prop];
            siteRowAdminProfileMap[prop] = siteInfo?.adminProfile ?? ADMIN_PROFILE_NONE_VALUE;
        }
        return { ...state, siteRowCheckboxPropMap, siteRowDropdownPropMap, siteRowAdminProfileMap, adminCheckboxState };
    }

    private static getAdminCheckboxStateOnModify(userRoleSelections: UserRoleSelections, adminCheckboxState: CheckboxState) {
        if (userRoleSelections.numSelectedServerAdmins === 0) {
            adminCheckboxState = CheckboxState.empty;
        } else if (userRoleSelections.numSelectedServerAdmins < userRoleSelections.numSelectedUsers) {
            adminCheckboxState = CheckboxState.mixed;
        } else {
            adminCheckboxState = CheckboxState.checked;
        }
        return adminCheckboxState;
    }

    public static getRowDropdownPropsOnModify(userRoleSelections: UserRoleSelections, siteId: string, siteRowDropdownPropMap: SiteRoleSelectorPropsDictionary, state: SiteRolesPickerContainerState, siteRolesResource: SiteRoleOptionsResource): SiteRolePickerMenu.Props {
        const siteInfo = userRoleSelections.userCountsAndRolesBySiteId[siteId];
        const numSelectedSiteMembers = siteInfo ? siteInfo.count : 0;
        const siteRoleOptions = siteRolesResource.getAllSiteRoleOptionsExcept(SiteRoles.ServerAdministrator);
        if (numSelectedSiteMembers > 0 && numSelectedSiteMembers === userRoleSelections.numSelectedUsers) {
            // All selected users are members of this site → show their aggregated role.
            let selectedRole = siteRolesResource.getSiteRoleOptionBySiteRole(siteInfo.siteRole);
            return { ...state.siteRowDropdownPropMap[siteId], selectedRole: selectedRole, isDisabled: false, siteRoleOptions: siteRoleOptions };
        }
        else if (numSelectedSiteMembers > 0) {
            // Only some selected users are members of this site → show "Mixed" to indicate differing roles.
            let selectedRole = siteRolesResource.getSiteRoleOptionBySiteRole(SiteRolesResource.Mixed);
            return { ...state.siteRowDropdownPropMap[siteId], selectedRole: selectedRole, isDisabled: false, siteRoleOptions: siteRoleOptions };
        }
        else if (userRoleSelections.numSelectedUsers > 0) {
            // Selected users exist but none are members of this site → default to Unlicensed.
            let selectedRole = siteRolesResource.getSiteRoleOptionBySiteRole(SiteRoles.Unlicensed);
            return { ...state.siteRowDropdownPropMap[siteId], selectedRole: selectedRole, isDisabled: false, siteRoleOptions: siteRoleOptions };
        }
        else {
            // No users are selected at all (e.g. dialog opened with numSelectedUsers === 0) →
            // preserve whatever role is already set (typically the DefaultSiteRole from initialisation).
            return { ...state.siteRowDropdownPropMap[siteId], siteRoleOptions: siteRoleOptions };
        }
    }

    public static getRowCheckboxPropsOnModify(userRoleSelections: UserRoleSelections, siteId: string, siteRowCheckboxPropMap: SiteRoleCheckboxPropsDictionary, state: SiteRolesPickerContainerState): ReactCheckboxTristate.Props {
        const siteInfo = userRoleSelections.userCountsAndRolesBySiteId[siteId];
        const numSelectedSiteMembers = siteInfo ? siteInfo.count : 0;
        if (numSelectedSiteMembers > 0 && numSelectedSiteMembers === userRoleSelections.numSelectedUsers) {
            return { ...state.siteRowCheckboxPropMap[siteId], checkboxState: CheckboxState.checked, disabled: false };
        }
        else if (numSelectedSiteMembers > 0) {
            return { ...state.siteRowCheckboxPropMap[siteId], checkboxState: CheckboxState.mixed, disabled: false };
        }
        else {
            return { ...state.siteRowCheckboxPropMap[siteId], checkboxState: CheckboxState.empty, disabled: false };
        }
    }

    /** Update corresponding dropdown with selected role. */
    public static getStateOnSiteRoleSelected(state: SiteRolesPickerContainerState, site: ISiteNameWithId, siteRoleOption: SiteRoleOption, siteRolesResource: SiteRoleOptionsResource): SiteRolesPickerContainerState {
        let siteRowDropdownPropMap: SiteRoleSelectorPropsDictionary = { ...state.siteRowDropdownPropMap };
        let selectedRole = siteRolesResource.getSiteRoleOptionBySiteRole(siteRoleOption.role);
        siteRowDropdownPropMap[site.id] = { ...siteRowDropdownPropMap[site.id], selectedRole };
        return { ...state, siteRowDropdownPropMap };
    }

    /**
    *
    * @param state
    * @param row
    * @param siteRolesResource
    * Update checkbox and dropdown on click
    */
    public static getStateOnCellClicked(state: SiteRolesPickerContainerState, row: any, siteRolesResource: SiteRoleOptionsResource): SiteRolesPickerContainerState {
        let siteRowCheckboxPropMap: SiteRoleCheckboxPropsDictionary = this.updateCheckboxMap(row, state);
        let siteRowDropdownPropMap: SiteRoleSelectorPropsDictionary = this.updateDropdownMap(row, state, siteRolesResource);
        return { ...state, siteRowCheckboxPropMap, siteRowDropdownPropMap };
    }

    private static updateCheckboxMap(site: ISiteNameWithId, state: SiteRolesPickerContainerState): SiteRoleCheckboxPropsDictionary {
        let siteToCheckboxPropMap: SiteRoleCheckboxPropsDictionary = { ...state.siteRowCheckboxPropMap };
        let checkboxProps = siteToCheckboxPropMap[site.id];
        let checkboxState = CheckboxState.toggle(checkboxProps.checkboxState as string);
        siteToCheckboxPropMap[site.id] = { ...checkboxProps, checkboxState };
        return siteToCheckboxPropMap;
    }

    private static updateDropdownMap(site: ISiteNameWithId, state: SiteRolesPickerContainerState, siteRolesResource: SiteRoleOptionsResource): SiteRoleSelectorPropsDictionary {
        let siteToDropdownPropMap: SiteRoleSelectorPropsDictionary = { ...state.siteRowDropdownPropMap };
        let selectedRole = siteRolesResource.getSiteRoleOptionBySiteRole(DefaultSiteRole);
        siteToDropdownPropMap[site.id] = { ...siteToDropdownPropMap[site.id], selectedRole };
        return siteToDropdownPropMap;
    }
}
